import { isNumber, isString } from "../utils/schemaUtils";

// One issue or pull request. `reference` is the cross-repository owner/repo#123
// form, which is the only handle that identifies an object when an answer spans
// repositories. `kind` is "issue" or "pull_request", left open so a kind a newer
// backend adds still renders.
export interface GitHubIssue {
  id: string;
  repo: string;
  number: number;
  reference: string;
  kind: string;
  title: string;
  state: string;
  // Distinguishes a closed issue that was resolved from one that was declined;
  // always empty on a pull request.
  stateReason?: string;
  author?: string;
  assignees: string[];
  labels: string[];
  milestone?: string;
  commentCount: number;
  url: string;
  createdAt: string;
  updatedAt: string;
  closedAt?: string;
}

export const isGitHubIssue = (v: unknown): v is GitHubIssue => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return (
    isString(r.id) &&
    isString(r.repo) &&
    isNumber(r.number) &&
    isString(r.reference) &&
    isString(r.kind) &&
    isString(r.title) &&
    isString(r.state) &&
    (r.stateReason === undefined || isString(r.stateReason)) &&
    (r.author === undefined || isString(r.author)) &&
    Array.isArray(r.assignees) &&
    r.assignees.every(isString) &&
    Array.isArray(r.labels) &&
    r.labels.every(isString) &&
    (r.milestone === undefined || isString(r.milestone)) &&
    isNumber(r.commentCount) &&
    isString(r.url) &&
    isString(r.createdAt) &&
    isString(r.updatedAt) &&
    (r.closedAt === undefined || isString(r.closedAt))
  );
};

// `totalCount` is what GitHub reported matched, which can exceed everything
// paging will ever reach; `capped` says matches exist past the search index's
// ceiling, and `incomplete` that GitHub gave up part-way and answered with what
// it had. Either one makes the count a lower bound rather than an answer.
export interface GitHubIssuesPayload {
  type: "github_issues";
  issues: GitHubIssue[];
  totalCount: number;
  capped: boolean;
  incomplete: boolean;
  query?: string;
}

export const parseGitHubIssuesPayload = (v: unknown): GitHubIssuesPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "github_issues") return null;
  if (!Array.isArray(r.issues) || !r.issues.every(isGitHubIssue)) return null;
  if (!isNumber(r.totalCount)) return null;
  if (typeof r.capped !== "boolean") return null;
  if (typeof r.incomplete !== "boolean") return null;
  if (r.query !== undefined && !isString(r.query)) return null;
  return {
    type: "github_issues",
    issues: r.issues,
    totalCount: r.totalCount,
    capped: r.capped,
    incomplete: r.incomplete,
    query: r.query,
  };
};
